import React from "react";

import {
  Button,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
} from "semantic-ui-react";

// Serializer
import LeaveTypeSerializer from "issara-sdk/types/LeaveTypeSerializer_apps_HRM";

// Common
import ButtonLoadCheck from "react-lib/appcon/common/ButtonLoadCheck";

// Table Rendering
import { ShiftTimeMapType, ShiftTimeSaveType } from "./CardEmployeeLeaveRequest";
import {
  formatLeaveRequestData,
  LeaveRequestListType,
  RenderDisplayLeaveTime,
  RenderDisplayQuotaLeaveHour,
  RenderDisplayRequestedHour,
  RenderDisplayShiftTime,
  RenderDocument,
  RenderEmployee,
  RenderLeaveTime,
  RenderLeaveType,
  RenderShiftTime,
  RenderStatus,
  WorkShiftType,
} from "./LeaveRequestListRendering";

// Interface
import { PickedProps, SetProp } from "./sequence/WorkSchedule";

import { checkLeaveTimeOutsideShift } from "./HRMInterface";

// Style
import "./TableLeaveRequestList.scss";

// Type
type TableEmployeeLeaveRequestProps = PickedProps & {
  // seq
  setProp: SetProp;
  // func
  onApprove: (id: number) => void;
  onChangeShiftTime?: (leaveRequestId: number, values: ShiftTimeSaveType[]) => void;
  onOpenReject: (id: number) => void;
  onUnapprove: (id: number) => void;
  // data
  leaveRequestList: LeaveRequestListType[];
  leaveTypeList: LeaveTypeSerializer[];
  shiftTimeMap: ShiftTimeMapType;
};

type RenderActionButtonProps = PickedProps & {
  // seq
  setProp: SetProp;
  // func
  checkDisableApprove: (item: LeaveRequestListType) => boolean;
  onApprove: (id: number) => void;
  onOpenReject: (id: number) => void;
  onUnapprove: (id: number) => void;
  // data
  item: LeaveRequestListType;
  // condition
  isInvalidTime?: boolean;
};

/**
 * ================================================================================================
 *RENDERING
 *================================================================================================*
 */

const RenderActionButton = ({
  // seq
  buttonLoadCheck,
  setProp,
  // func
  checkDisableApprove,
  onApprove,
  onOpenReject,
  onUnapprove,
  // data
  item,
  // condition
  isInvalidTime,
}: RenderActionButtonProps) => {
  if (item.status === "X") {
    return null;
  }

  const isDisabled = checkDisableApprove(item) || isInvalidTime;

  if (item.status === "A") {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
        <ButtonLoadCheck
          id={`TableEmployeeLeaveRequest-ButtonLoadCheck-Unapprove-${item.id}`}
          // func
          setProp={setProp}
          color="red"
          name="Unapprove"
          // data
          paramKey={`CardWordSchedule_LeaveRequest_APPROVAL_${item.id}`}
          size="small"
          // config
          title="Unapprove"
          buttonLoadCheck={buttonLoadCheck?.[`CardWordSchedule_LeaveRequest_APPROVAL_${item.id}`]}
          onClick={() => {
            onUnapprove(item.id);
          }}
        />
      </div>
    );
  }

  if (item.status === "P") {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
        <ButtonLoadCheck
          id={`TableEmployeeLeaveRequest-ButtonLoadCheck-Approve-${item.id}`}
          setProp={setProp}
          color="green"
          // func
          disabled={isDisabled}
          name="Approve"
          // data
          paramKey={`CardWordSchedule_LeaveRequest_APPROVAL_${item.id}`}
          size="small"
          // config
          title="Approve"
          buttonLoadCheck={buttonLoadCheck?.[`CardWordSchedule_LeaveRequest_APPROVAL_${item.id}`]}
          onClick={() => {
            onApprove(item.id);
          }}
        />
        <Button
          id={`TableEmployeeLeaveRequest-Button-Reject-${item.id}`}
          color="orange"
          size="small"
          onClick={() => {
            onOpenReject(item.id);
          }}
        >
          Reject
        </Button>
      </div>
    );
  }

  return null;
};

/**
 * ================================================================================================
 *HANDLER
 *================================================================================================*
 */

const checkDocumentRequired =
  (leaveTypeList: LeaveTypeSerializer[]) =>
  (item: LeaveRequestListType): boolean => {
    // หาประเภทการลาที่ตรงกับคำขอลาปัจจุบัน
    const matchedLeaveType = leaveTypeList.find((type) => type.id === item.leave_type);

    // ตรวจสอบว่าประเภทการลานี้จำเป็นต้องแนบเอกสารหรือไม่
    const isRequired = !!matchedLeaveType?.attachment_required;

    const hasDocs = item.documents.length > 0;

    // คืนค่า true เมื่อต้องปิดการใช้งานปุ่มอนุมัติ:
    // - ประเภทการลาต้องแนบเอกสาร (isRequired = true)
    // - แต่ไม่มีเอกสารแนบมา (!hasDocs = true)
    // คืนค่า false เมื่ออนุมัติได้:
    // - ไม่ต้องแนับเอกสาร หรือ มีเอกสารแนบมาครบ
    return (isRequired && !hasDocs) || false;
  };

export const checkIsInvalidShiftTime = (
  shiftTime: ShiftTimeSaveType[],
  workShift: WorkShiftType[]
) => {
  if (workShift.length === 0) {
    return false;
  }

  const grouped: Partial<Record<string, ShiftTimeSaveType[]>> = {};

  for (const st of shiftTime) {
    grouped[st.date] ??= [];
    grouped[st.date]?.push(st);
  }

  return Object.values(grouped).some((values) => {
    const hasNoLeaveTime = (values ?? []).every(
      (st) => !st.leave_end_time && !st.leave_start_time && st.id !== 0
    );

    if (hasNoLeaveTime) {
      return true;
    }

    return (values ?? []).some((st) => {
      const shift = workShift.find((ws) => ws.id === st.id);

      if (st.id === 0) {
        return false;
      }

      if (!shift || !st.leave_start_time || !st.leave_end_time) {
        return true;
      }

      const { isEndOutside, isStartOutside } = checkLeaveTimeOutsideShift(
        st.leave_start_time,
        st.leave_end_time,
        shift
      );

      return isEndOutside || isStartOutside;
    });
  });
};

// export const checkIsInvalidHour = (
//   shiftTime: ShiftTimeSaveType[],
//   workShift: WorkShiftType[],
//   expectedMinutes: number
// ) => {
//   if (shiftTime.length === 0 || workShift.length === 0) {
//     return false;
//   }

//   // if (shiftTime.some((st) => st.id === 0)) return false;
//   const isHalfDay = expectedMinutes === 240;

//   const totalLeaveMinutes = shiftTime.reduce((acc, st) => {
//     if (st.id === 0 && isHalfDay) {
//       return acc + 4 * 60;
//     } else if (st.id === 0) {
//       return acc + 8 * 60;
//     } else if (st.id) {
//       const shift = workShift.find((ws) => ws.id === st.id);

//       if (!shift || !st.leave_start_time || !st.leave_end_time) {
//         return acc;
//       }

//       const { hours, minutes } = calculateTotalWorkingTime({
//         endBreakTime: shift.end_break_time,
//         endTime: st.leave_end_time,
//         isBreakTime: shift.is_break_time,
//         startBreakTime: shift.start_break_time,
//         startTime: st.leave_start_time,
//       });

//       return acc + hours * 60 + minutes;
//     }

//     return acc;
//   }, 0);

//   return totalLeaveMinutes < expectedMinutes;
// };

/**
 * ================================================================================================
 *MAIN COMPONENT
 *================================================================================================*
 */

const TableEmployeeLeaveRequest = (props: TableEmployeeLeaveRequestProps) => {
  const { leaveRequestList, leaveTypeList, shiftTimeMap } = props;
  const checkDisableApprove = checkDocumentRequired(leaveTypeList);

  return (
    <div className="table-elr">
      <Table celled compact structured>
        <TableHeader>
          <TableRow>
            <TableHeaderCell textAlign="center">Employee</TableHeaderCell>
            <TableHeaderCell textAlign="center">Leave Type</TableHeaderCell>
            <TableHeaderCell style={{ width: "95px" }} textAlign="center">
              Remaining Leave Days
            </TableHeaderCell>
            <TableHeaderCell textAlign="center">Leave Date</TableHeaderCell>
            <TableHeaderCell textAlign="center">Total Day</TableHeaderCell>
            <TableHeaderCell textAlign="center">Shift Time</TableHeaderCell>
            <TableHeaderCell textAlign="center" width={2}>
              Leave Time
            </TableHeaderCell>
            <TableHeaderCell style={{ width: "85px" }} textAlign="center">
              Requested hour
            </TableHeaderCell>
            <TableHeaderCell style={{ width: "115px" }} textAlign="center">
              Under/Over quota leave (hr.)
            </TableHeaderCell>
            <TableHeaderCell textAlign="center">Document</TableHeaderCell>
            <TableHeaderCell textAlign="center">Status</TableHeaderCell>
            <TableHeaderCell textAlign="center" />
          </TableRow>
        </TableHeader>

        <TableBody>
          {leaveRequestList.length > 0 ? (
            formatLeaveRequestData(leaveRequestList, shiftTimeMap).map((item) =>
              item.leaveDates.map((date, index) => {
                const isFirstRow = index === 0;
                const isNoShiftTime =
                  item.work_shift_detail.work_shift_list?.length === 0 &&
                  !item.work_shift_detail.work_shift;
                const hasShiftTime = item.shiftTimeByDate[date.format("YYYY-MM-DD")].length > 0;

                return (
                  <TableRow
                    key={`${item.id}-${date.format("YYYY-MM-DD")}`}
                    style={{ borderBottom: "1px solid #e0e0e0" }}
                  >
                    {isFirstRow && (
                      <>
                        <TableCell
                          rowSpan={item.leaveDates.length}
                          textAlign="center"
                          verticalAlign="top"
                        >
                          <RenderEmployee
                            fullName={item.created_by_full_name}
                            employeeCode={item.employee_code}
                          />
                        </TableCell>
                        <TableCell
                          rowSpan={item.leaveDates.length}
                          textAlign="center"
                          verticalAlign="top"
                        >
                          <RenderLeaveType item={item} leaveTypeOptions={leaveTypeList} />
                        </TableCell>
                        <TableCell
                          rowSpan={item.leaveDates.length}
                          textAlign="center"
                          verticalAlign="top"
                        >
                          {item.total_remaining_leave_days}
                        </TableCell>
                      </>
                    )}

                    <TableCell textAlign="center">{date.format("DD/MM/YYYY")}</TableCell>
                    {isFirstRow && (
                      <TableCell
                        rowSpan={item.leaveDates.length}
                        textAlign="center"
                        verticalAlign="top"
                      >
                        {item.total_leave_days}
                      </TableCell>
                    )}
                    <TableCell textAlign="center" verticalAlign="top">
                      {item.status === "P" && !isNoShiftTime && hasShiftTime ? (
                        <RenderShiftTime
                          key={date.format("YYYY-MM-DD")}
                          date={date}
                          shiftTime={item.shiftTimeByDate[date.format("YYYY-MM-DD")]}
                          status={item.status}
                          workShiftItem={item.work_shift_detail}
                          onChange={(values) => props.onChangeShiftTime?.(item.id, values)}
                        />
                      ) : (
                        <RenderDisplayShiftTime
                          date={date}
                          workShiftList={item.work_shift_detail.work_shift ?? []}
                        />
                      )}
                    </TableCell>
                    <TableCell textAlign="center" verticalAlign="top">
                      {item.status === "P" && !isNoShiftTime && hasShiftTime ? (
                        <RenderLeaveTime
                          date={date}
                          shiftTime={item.shiftTimeMapByDate[date.format("YYYY-MM-DD")]}
                          status={item.status}
                          workShiftItem={item.work_shift_detail}
                          onChange={(values) => props.onChangeShiftTime?.(item.id, values)}
                        />
                      ) : (
                        <RenderDisplayLeaveTime
                          date={date}
                          workShiftList={item.work_shift_detail.work_shift ?? []}
                        />
                      )}
                    </TableCell>
                    <TableCell textAlign="center" verticalAlign="top">
                      <RenderDisplayRequestedHour
                        date={date}
                        shiftTime={item.shiftTimeMapByDate[date.format("YYYY-MM-DD")]}
                        status={item.status}
                        totalLeaveDays={item.total_leave_days}
                        workShiftItem={item.work_shift_detail}
                      />
                    </TableCell>
                    <TableCell textAlign="center" verticalAlign="top">
                      <RenderDisplayQuotaLeaveHour
                        date={date}
                        shiftTime={item.shiftTimeMapByDate[date.format("YYYY-MM-DD")]}
                        status={item.status}
                        totalLeaveDays={item.total_leave_days}
                        workShiftItem={item.work_shift_detail}
                      />
                    </TableCell>

                    {isFirstRow && (
                      <>
                        <TableCell rowSpan={item.leaveDates.length} verticalAlign="top">
                          <RenderDocument item={item} leaveTypeList={leaveTypeList} />
                        </TableCell>
                        <TableCell
                          rowSpan={item.leaveDates.length}
                          textAlign="center"
                          verticalAlign="top"
                        >
                          <RenderStatus item={item} remark={item.reject_remark} status={item.status} />
                        </TableCell>
                        <TableCell
                          rowSpan={item.leaveDates.length}
                          textAlign="center"
                          verticalAlign="top"
                        >
                          <RenderActionButton
                            setProp={props.setProp}
                            checkDisableApprove={checkDisableApprove}
                            isInvalidTime={item.isInvalidTime}
                            item={item}
                            onApprove={props.onApprove}
                            onOpenReject={props.onOpenReject}
                            onUnapprove={props.onUnapprove}
                          />
                        </TableCell>
                      </>
                    )}
                  </TableRow>
                );
              })
            )
          ) : (
            <TableRow>
              <TableCell style={{ color: "grey" }} colSpan="11" textAlign="center">
                No rows found
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
};

TableEmployeeLeaveRequest.displayName = "TableEmployeeLeaveRequest";

export default React.memo(TableEmployeeLeaveRequest);
